import { faEdit, faMagic, faRedo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "@ui";
import { StyledInput } from "@ui/Form";
import { FormInput } from "components/Form/next";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import useSWR from "swr";
import { UserInvoiceIssuer } from "../types";

interface Props {
    name?: string;
    issuer?: { id: number } & Partial<UserInvoiceIssuer>;
    clubSlug: string | null;
    label?: string;
    number?: string;
    preview?: string;
    previewStart?: string;
    isCredit?: boolean;
    manual?: boolean;
}

export const InvoiceNumberInput = ({
    name,
    label,
    issuer,
    clubSlug,
    number,
    preview = "",
    previewStart = "",
    isCredit = false,
    manual: _manual = false,
}: Props) => {
    const { t } = useTranslation("invoice");
    const [visible, setVisible] = useState(false);
    const [manual, setManual] = useState(_manual);
    const {
        setValue,
        watch,
        formState: { submitCount },
    } = useFormContext();
    const previewValue = watch(preview);
    const previewStartValue = watch(previewStart);
    const getParams = () => {
        const params = new URLSearchParams();
        params.append("is_credit", String(isCredit));
        if (preview) params.append("pattern", previewValue);
        if (previewStart) params.append("next", previewStartValue);

        return params;
    };
    const { data, mutate } = useSWR(
        clubSlug && issuer && !manual && !number ? `invoice/${clubSlug}/number/${issuer.id}${"?" + getParams()}` : null
    );

    useEffect(() => {
        mutate();
    }, [submitCount]);

    useEffect(() => {
        if (!preview) {
            if (data && !manual) setValue(name, data.number);
            else if (number) setValue(name, number);
        }
    }, [data, manual, number, preview]);

    // Flag the backend whether the number was entered manually. In auto mode the field still
    // holds the generated preview, so without this flag the server can't tell a user override
    // apart from the auto-suggestion and would skip race-safe generation.
    useEffect(() => {
        if (!preview && name) setValue(`${name}_manual`, manual);
    }, [manual, preview, name, setValue]);

    const toggleManualMode = () => setManual(!manual);

    return (
        <div>
            {preview !== "" ? (
                <StyledInput value={data?.number || t("invoiceForm.numberInput.loading")} disabled />
            ) : (
                <FormInput
                    name={name}
                    placeholder={label}
                    disabled={!manual}
                    contentRight={
                        !preview && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Button
                                    type="button"
                                    disabled={manual}
                                    icon={<FontAwesomeIcon icon={faRedo} />}
                                    onClick={() => mutate()}
                                    size="xs"
                                />
                                <Tooltip
                                    disabled={manual}
                                    trigger={manual ? null : "hover"}
                                    tooltip={<ManualModeWarning t={t} />}
                                    tooltipClassName="tw-max-w-[300px] !tw-p-4"
                                >
                                    <Button
                                        size="xs"
                                        type="button"
                                        color="warning"
                                        onClick={toggleManualMode}
                                        icon={<FontAwesomeIcon icon={manual ? faMagic : faEdit} />}
                                    />
                                </Tooltip>
                            </div>
                        )
                    }
                />
            )}
        </div>
    );
};

const ManualModeWarning = ({ t }: { t: TFunction<"invoice"> }) => (
    <>
        <p>
            <Trans
                t={t}
                i18nKey="invoiceForm.numberInput.manualWarningText"
                components={{ b: <b /> }}
            />
        </p>
        <p>{t("invoiceForm.numberInput.manualWarningHint")}</p>
    </>
);
